import { ListItemText, MenuItem, SelectChangeEvent, SxProps, Theme } from '@mui/material';
import Select from '../../inputs/Select';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { GraphQLSelectOption } from './BannedCrewSelect';

type FormikSelectProps<T> = BaseFormikFieldProps<T> & {
  options: GraphQLSelectOption[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  className?: string;
  placeholder?: string;
  required?: boolean;
  inputProps?: Record<string, unknown>;
  sx?: SxProps<Theme>;
  value?: unknown;
  name?: string;
  testId?: string;
};

export const FormikSelect = <T extends Record<string, unknown>>({
  id,
  label,
  options,
  onChange,
  placeholder,
  required,
  sx,
  value,
  name,
  testId,
  ...rest
}: FormikSelectProps<T>) => {
  const { [TID_ATTR]: restTestId, ...restWithoutTestId } = rest as Record<string, unknown> & { [TID_ATTR]?: string };
  const baseTestId = testId ?? restTestId;
  const { status, setFieldValue, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  const handleOnChange = (event: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const selectEvent = event as SelectChangeEvent<unknown>;
    setFieldValue(id as string, selectEvent.target.value);
    if (onChange) onChange(selectEvent);
  };
  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  //Ensure null values are converted to empty string to avoid MUI warnings
  const normalizedValue = (() => {
    if (value !== undefined) {
      return value === null ? '' : value;
    }
    return fieldValue === null || fieldValue === undefined ? '' : fieldValue;
  })();

  const existingSlotProps = (restWithoutTestId.slotProps ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const slotProps = {
    ...existingSlotProps,
    ...(baseTestId && {
      inputLabel: { ...existingSlotProps.inputLabel, [TID_ATTR]: `${baseTestId}:label` },
      selectDisplay: { ...existingSlotProps.selectDisplay, [TID_ATTR]: baseTestId },
      formHelperText: { ...existingSlotProps.formHelperText, [TID_ATTR]: `${baseTestId}:helper-text` }
    })
  };

  return (
    <Select
      {...restWithoutTestId}
      fullWidth
      id={id}
      name={name || id}
      label={label || undefined}
      value={normalizedValue}
      onChange={handleOnChange}
      onBlur={handleBlur}
      error={hasError}
      helperText={errorText}
      placeholder={placeholder}
      className={!label ? 'formik-select-no-label' : undefined}
      required={required}
      sx={sx}
      slotProps={slotProps}
    >
      {options.map((option) =>
        typeof option === 'object' ? (
          <MenuItem key={option.id} value={option.id}>
            <ListItemText primary={option.name ?? undefined} sx={{ margin: 0 }} />
          </MenuItem>
        ) : (
          <MenuItem key={option} value={option}>
            <ListItemText primary={option} sx={{ margin: 0 }} />
          </MenuItem>
        )
      )}
    </Select>
  );
};
